import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import {
  AppDialogSection,
  AppDialog,
  AppDialogActions,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { Box } from '@/UI/Box/Box';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { TextSmMd, TextStdMd, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCol } from '@/UI/styles';
import { color } from '@/colors';
import { Input } from '@/UI/Input/Input';
import { formatPrice } from '@/consts/pricing.consts';
import { BillingCycle } from '@/types/shared';
import { ButtonContained } from '@/UI/Buttons/AppButton';

const DEFAULT_TEAM_SIZE = 5;
const MIN_TEAM_SIZE = 1;

interface TeamSizeModalProps {
  open: boolean;
  onClose: () => void;
  packagePrice: number;
  annualTotal: number;
  billingCycle: BillingCycle;
  isProcessing: boolean;
  getStripeCheckoutUrl: (numberOfSeats: number) => Promise<string | null>;
  isDiscountActive: boolean;
}

function TeamSizeModal({
  open,
  onClose,
  packagePrice,
  annualTotal,
  isProcessing,
  billingCycle,
  getStripeCheckoutUrl,
  isDiscountActive,
}: TeamSizeModalProps) {
  const { t } = useTranslation();

  const [teamSizeInput, setTeamSizeInput] = useState(String(DEFAULT_TEAM_SIZE));
  const teamSize = Math.max(MIN_TEAM_SIZE, parseInt(teamSizeInput, 10) || MIN_TEAM_SIZE);
  const [totalPrice, setTotalPrice] = useState(packagePrice * teamSize);

  useEffect(() => {
    setTotalPrice(packagePrice * teamSize);
  }, [teamSize, packagePrice]);

  const handlePayNowButtonClick = async () => {
    const checkoutSessionUrl = await getStripeCheckoutUrl(teamSize);
    if (checkoutSessionUrl) {
      window.location.href = checkoutSessionUrl;
    }
  };

  return (
    <AppDialog open={open} onClose={onClose} width="md" slotProps={{ paper: { sx: { backgroundColor: token.bg } } }}>
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 5, pb: 0 }}>
          <AppDialogTitle>{t(I18N_KEYS.PAYMENTS.TEAM_SIZE_MODAL.TITLE)}</AppDialogTitle>
          <AppDialogXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} />
        </AppDialogHeader>
        <AppDialogContent sx={{ px: 3, pb: 0, '&&': { pt: 1.5 } }}>
          <FlexCol sx={{ gap: 2 }}>
            <TextStdRg color="inherit">
              {t(I18N_KEYS.PAYMENTS.TEAM_SIZE_MODAL.SUBTITLE, {
                pricePerSeat: formatPrice(billingCycle === BillingCycle.Yearly ? annualTotal : packagePrice),
                billingCycle:
                  billingCycle === BillingCycle.Yearly ? t(I18N_KEYS.GENERAL.YEAR) : t(I18N_KEYS.GENERAL.MONTH),
              })}
            </TextStdRg>
            <Input
              type="number"
              size="large"
              fullWidth
              value={teamSizeInput}
              onChange={(e) => setTeamSizeInput(e.target.value)}
              onBlur={() => setTeamSizeInput(String(teamSize))}
              inputProps={{ min: MIN_TEAM_SIZE }}
            />
          </FlexCol>
          <Box sx={{ my: 2, width: '100%' }}>
            <AppDivider />
          </Box>
          <Flex sx={{ justifyContent: 'space-between', mb: isDiscountActive ? 0.5 : 1 }}>
            <TextStdMd color={token.text}>{t(I18N_KEYS.GENERAL.TOTAL)}</TextStdMd>
            {billingCycle === BillingCycle.Yearly ? (
              <TextStdMd color={token.text}>
                {formatPrice(annualTotal)} x {teamSize} = {formatPrice(annualTotal * teamSize)} /{' '}
                {t(I18N_KEYS.GENERAL.YEAR)}
              </TextStdMd>
            ) : (
              <TextStdMd color={token.text}>
                {formatPrice(packagePrice)} x {teamSize} = {formatPrice(totalPrice)} / {t(I18N_KEYS.GENERAL.MONTH)}
              </TextStdMd>
            )}
          </Flex>
          {isDiscountActive && (
            <Box sx={{ mb: 1 }}>
              <TextSmMd color={token.textSecondary} sx={{ fontSize: '10px' }}>
                {t(I18N_KEYS.PAYMENTS.TEAM_SIZE_MODAL.DISCOUNT_DISCLAIMER)}
              </TextSmMd>
            </Box>
          )}
        </AppDialogContent>
        <AppDialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 3 }}>
          <ButtonContained size="small" mode="outlined" onClick={onClose} disabled={isProcessing}>
            {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CTA_CANCEL)}
          </ButtonContained>
          <ButtonContained
            size="small"
            startIcon={isProcessing ? <CircularProgress size={16} sx={{ color: color.Black92 }} /> : null}
            onClick={() => void handlePayNowButtonClick()}
            disabled={isProcessing}
          >
            {isProcessing
              ? t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PROCESSING)
              : t(I18N_KEYS.PAYMENTS.TEAM_SIZE_MODAL.CTA_CONFIRM)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}

export default TeamSizeModal;
